import React, { useEffect, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import { listUsersForAssignment } from 'core/users/listUsersForAssignment'
import {
  assistantRunNowDisabled,
  createAssistantProfile,
  createAssistantRoutine,
  suggestRoutineName,
  deleteAssistantProfile,
  deleteAssistantRoutine,
  EMPTY_ASSISTANT_GUIDE,
  inviteAssistantMember,
  removeAssistantMember,
  runAssistantRoutineNow,
  subscribeAssistantRoutineRuns,
  subscribeAssistantRoutines,
  updateAssistantProfile,
  updateAssistantRoutine,
  type AssistantGuide,
  type AssistantProfileSummary,
  type AssistantReportTopic,
  type AssistantRoutinePeriod,
  type AssistantRoutineSchedule,
  type AssistantRoutineClock,
  type AssistantRoutineKind,
  type AssistantRoutineRun,
  type AssistantRoutineSummary,
} from './assistantProfiles'
import {
  choiceIds,
  DEPARTMENT_OPTIONS,
  isAllSelected,
  TONE_OPTIONS,
  toggleAllChoice,
  watchOptionsForDepartments,
  type GuideOption,
} from './assistantGuideOptions'
import { describeRoutineClock, RoutinePeriodField, RoutineScheduleFields } from './RoutineScheduleFields'
import { RoutineRecipientFields, type RoutineRecipients } from './RoutineRecipientFields'

type AdminOption = { id: string; label: string }
type EditorTab = 'general' | 'routines' | 'jobs'

const MEMBER_CAP = 12
const TOPICS: Array<{ id: AssistantReportTopic; label: string }> = [
  { id: 'sales', label: 'Sales' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'custom', label: 'Custom' },
]

function GuideChoices({
  question,
  help,
  options,
  value,
  onChange,
}: {
  question: string
  help: string
  options: GuideOption[]
  value: string
  onChange: (value: string) => void
}) {
  const ids = options.map(option => option.id)
  const selected = new Set(choiceIds(value))
  return (
    <FormControl component='fieldset' variant='standard'>
      <FormLabel component='legend' sx={{ typography: 'subtitle2', color: 'text.primary' }}>
        {question}
      </FormLabel>
      <FormHelperText sx={{ mx: 0, mt: 0.25, mb: 0.5 }}>{help}</FormHelperText>
      <FormGroup row>
        <FormControlLabel
          control={<Checkbox size='small' checked={isAllSelected(value)} onChange={() => onChange('all')} />}
          label='All'
        />
        {options.map(option => (
          <FormControlLabel
            key={option.id}
            control={
              <Checkbox
                size='small'
                checked={selected.has(option.id)}
                onChange={() => onChange(toggleAllChoice(value, option.id, ids))}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </FormControl>
  )
}

function formatRoutineCode(code: string): string {
  const trimmed = code.trim()
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2)
  } catch {
    return trimmed
  }
}

const STATUS_LABEL: Record<AssistantRoutineRun['status'], string> = {
  running: 'Running',
  sent: 'Sent',
  error: 'Error',
}

function routineTitle(routine: AssistantRoutineSummary): string {
  if (routine.name.trim()) return routine.name.trim()
  const kind = routine.kind === 'report' ? `${routine.reportTopic || 'custom'} report` : 'Email'
  return `${kind} · ${describeRoutineClock({
    schedule: routine.schedule,
    times: routine.times,
    days: routine.days,
    date: routine.date,
  })}`
}

export const AssistantProfileDialog: React.FC<{
  open: boolean
  profile: AssistantProfileSummary | null
  uid: string
  onClose: () => void
  onCreated: (profile: { id: string; chatId: string }) => void
  onDeleted: () => void
  startComposer?: boolean
  composerDraft?: {
    prompt: string
    period: AssistantRoutinePeriod
    schedule: AssistantRoutineSchedule
    kind: AssistantRoutineKind
    topic: AssistantReportTopic
  } | null
}> = ({ open, profile, uid, onClose, onCreated, onDeleted, startComposer = false, composerDraft = null }) => {
  const [tab, setTab] = useState<EditorTab>('general')
  const [name, setName] = useState('')
  const [details, setDetails] = useState('')
  const [guide, setGuide] = useState<AssistantGuide>(EMPTY_ASSISTANT_GUIDE)
  const [composing, setComposing] = useState(false)
  const [enhance, setEnhance] = useState('')
  const [editRecipients, setEditRecipients] = useState<RoutineRecipients>({ userIds: [], emails: [] })
  const [admins, setAdmins] = useState<AdminOption[]>([])
  const [member, setMember] = useState<AdminOption | null>(null)
  const [routines, setRoutines] = useState<AssistantRoutineSummary[]>([])
  const [runs, setRuns] = useState<AssistantRoutineRun[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showCode, setShowCode] = useState(false)
  const [clickedAt, setClickedAt] = useState<Record<string, number>>({})
  const [now, setNow] = useState(() => Date.now())
  const [kind, setKind] = useState<AssistantRoutineKind>('email')
  const [topic, setTopic] = useState<AssistantReportTopic>('sales')
  const [prompt, setPrompt] = useState('')
  const [routineName, setRoutineName] = useState('')
  const [naming, setNaming] = useState(false)
  const [editName, setEditName] = useState('')
  const [period, setPeriod] = useState<AssistantRoutinePeriod>('last_24_hours')
  const [clock, setClock] = useState<AssistantRoutineClock>({
    schedule: 'weekdays',
    times: ['08:00'],
    days: [],
    date: null,
  })
  const [recipients, setRecipients] = useState<RoutineRecipients>({ userIds: [], emails: [] })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTab(startComposer && profile ? 'routines' : 'general')
    setComposing(Boolean(startComposer && profile))
    setName(profile?.name ?? '')
    setDetails(profile?.details ?? '')
    setGuide(profile?.guide ?? EMPTY_ASSISTANT_GUIDE)
    setEnhance('')
    setEditRecipients({ userIds: [], emails: [] })
    setMember(null)
    setRoutineName('')
    setNaming(false)
    setEditName('')
    setPrompt(startComposer && composerDraft ? composerDraft.prompt : '')
    setPeriod(startComposer && composerDraft ? composerDraft.period : 'last_24_hours')
    setKind(startComposer && composerDraft ? composerDraft.kind : 'email')
    setTopic(startComposer && composerDraft ? composerDraft.topic : 'sales')
    setClock({
      schedule: startComposer && composerDraft ? composerDraft.schedule : 'weekdays',
      times: ['08:00'],
      days: [],
      date: null,
    })
    setRecipients({ userIds: [], emails: [] })
    setSelectedId(null)
    setShowCode(false)
    setClickedAt({})
    setError(null)
  }, [open, profile?.id, startComposer, composerDraft])

  useEffect(() => {
    if (!open || !startComposer || !composerDraft?.prompt) return
    let cancelled = false
    setNaming(true)
    void suggestRoutineName(composerDraft.prompt)
      .then(next => {
        if (cancelled || !next.trim()) return
        setRoutineName(current => (current.trim() ? current : next.trim()))
      })
      .catch(() => {
        if (!cancelled) setError('Could not name that routine. Enter a name.')
      })
      .finally(() => {
        if (!cancelled) setNaming(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, startComposer, composerDraft])

  useEffect(() => {
    if (!open) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    void listUsersForAssignment()
      .then(users => {
        if (cancelled) return
        setAdmins(
          users
            .filter(user => user.role === 'admin')
            .map(user => ({
              id: user.id,
              label: user.name || user.email || 'Admin',
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        )
      })
      .catch(() => {
        if (!cancelled) setAdmins([])
      })
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    if (!open || !profile) {
      setRoutines([])
      return
    }
    return subscribeAssistantRoutines(profile.id, uid, setRoutines, () =>
      setError('Could not load routines'),
    )
  }, [open, profile?.id, uid])

  useEffect(() => {
    if (!open || !profile) {
      setRuns([])
      return
    }
    return subscribeAssistantRoutineRuns(profile.id, uid, setRuns, () => setError('Could not load jobs'))
  }, [open, profile?.id, uid])

  useEffect(() => {
    const routine = routines.find(item => item.id === selectedId)
    setEnhance('')
    setEditName(routine?.name ?? '')
    setEditRecipients({
      userIds: routine?.recipientUserIds ?? [],
      emails: routine?.recipientEmails ?? [],
    })
  }, [selectedId])

  const isOwner = !profile || profile.ownerId === uid
  const sharedIds = profile?.memberIds.filter(id => id !== profile.ownerId) ?? []
  const adminLabel = (id: string) => admins.find(admin => admin.id === id)?.label || 'Admin'
  const pickerOptions = admins.filter(
    admin => admin.id !== uid && admin.id !== profile?.ownerId && !profile?.memberIds.includes(admin.id),
  )
  const selected = routines.find(routine => routine.id === selectedId) ?? null

  const saveProfile = async () => {
    setBusy(true)
    setError(null)
    try {
      if (!profile) {
        const created = await createAssistantProfile({
          name,
          details,
          guide,
          ...(member ? { memberId: member.id } : {}),
        })
        onCreated(created)
        return
      }
      await updateAssistantProfile(profile.id, { name, details, guide })
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save that assistant')
    } finally {
      setBusy(false)
    }
  }

  const addMember = async () => {
    if (!profile || !member || !isOwner) return
    setBusy(true)
    setError(null)
    try {
      await inviteAssistantMember(profile.id, member.id)
      setMember(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not add that admin')
    } finally {
      setBusy(false)
    }
  }

  const addRoutine = async () => {
    if (!profile) return
    setBusy(true)
    setError(null)
    try {
      await createAssistantRoutine(profile.id, {
        kind,
        name: routineName.trim(),
        prompt,
        period,
        schedule: clock.schedule,
        times: clock.times,
        days: clock.days,
        date: clock.date,
        recipientUserIds: recipients.userIds,
        recipientEmails: recipients.emails,
        ...(kind === 'report' ? { reportTopic: topic } : {}),
      })
      setPrompt('')
      setRoutineName('')
      setComposing(false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save that routine')
    } finally {
      setBusy(false)
    }
  }

  const routinePayload = (routine: AssistantRoutineSummary, recipients: RoutineRecipients, note?: string) => ({
    kind: routine.kind,
    name: editName.trim() || routine.name,
    prompt: routine.prompt,
    period: routine.period,
    schedule: routine.schedule,
    times: routine.times,
    days: routine.days,
    date: routine.date,
    recipientUserIds: recipients.userIds,
    recipientEmails: recipients.emails,
    reportTopic: routine.reportTopic,
    enabled: routine.enabled,
    ...(note ? { enhance: note } : {}),
  })

  const saveRoutineUpdate = async () => {
    if (!selected) return
    const note = enhance.trim()
    if (!note) {
      setError('Describe how the instructions should change')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await updateAssistantRoutine(selected.id, routinePayload(selected, editRecipients, note))
      setEnhance('')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update that routine')
    } finally {
      setBusy(false)
    }
  }

  const saveRecipients = async () => {
    if (!selected) return
    if (editRecipients.userIds.length === 0 && editRecipients.emails.length === 0) {
      setError('Keep at least one recipient')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await updateAssistantRoutine(selected.id, routinePayload(selected, editRecipients))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update those recipients')
    } finally {
      setBusy(false)
    }
  }

  const runNow = (routineId: string) => {
    setClickedAt(current => ({ ...current, [routineId]: Date.now() }))
    setTab('jobs')
    setError(null)
    void runAssistantRoutineNow(routineId).catch(caught =>
      setError(caught instanceof Error ? caught.message : 'Could not run that routine'),
    )
  }

  const runDisabled = (routine: AssistantRoutineSummary) => {
    const clicked = clickedAt[routine.id]
    const localUntil = clicked ? clicked + 60_000 : 0
    const until = Math.max(routine.manualLockedUntil ?? 0, localUntil)
    return assistantRunNowDisabled({
      now,
      manualLockedUntil: until > 0 ? until : null,
      running: runs.some(run => run.routineId === routine.id && run.status === 'running'),
    })
  }

  const sharing = (
    <Stack spacing={1}>
      {profile && sharedIds.length > 0 ? (
        <Stack direction='row' spacing={0.75} useFlexGap flexWrap='wrap'>
          {sharedIds.map(id => (
            <Chip
              key={id}
              size='small'
              label={adminLabel(id)}
              onDelete={
                isOwner
                  ? () => {
                      setBusy(true)
                      void removeAssistantMember(profile.id, id)
                        .catch(caught =>
                          setError(caught instanceof Error ? caught.message : 'Could not remove that admin'),
                        )
                        .finally(() => setBusy(false))
                    }
                  : undefined
              }
            />
          ))}
        </Stack>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          Not shared yet
        </Typography>
      )}
      {isOwner && (!profile || profile.memberIds.length < MEMBER_CAP) ? (
        <Stack direction='row' spacing={1} alignItems='center'>
          <Autocomplete
            options={profile ? pickerOptions : admins.filter(admin => admin.id !== uid)}
            value={member}
            onChange={(_, value) => setMember(value)}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ flex: 1 }}
            renderInput={params => (
              <TextField {...params} label='Add admin' size='small' placeholder='Optional' />
            )}
          />
          {profile ? (
            <Button size='small' disabled={busy || !member} onClick={() => void addMember()} sx={{ textTransform: 'none' }}>
              Add
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  )

  const routineForm = (
    <Stack spacing={1} sx={{ mt: 1.5 }}>
      <Typography variant='subtitle2'>New routine</Typography>
      <TextField
        label='Name'
        value={routineName}
        onChange={event => setRoutineName(event.target.value)}
        placeholder='Weekday sales digest'
        helperText='Shown in the routine list.'
        size='small'
        fullWidth
        disabled={naming}
        inputProps={{ maxLength: 60 }}
        InputProps={{
          endAdornment: naming ? (
            <InputAdornment position='end'>
              <CircularProgress size={16} />
            </InputAdornment>
          ) : undefined,
        }}
      />
      <Stack direction='row' spacing={1}>
        <FormControl size='small' sx={{ minWidth: 120 }}>
          <InputLabel>Kind</InputLabel>
          <Select label='Kind' value={kind} onChange={event => setKind(event.target.value as AssistantRoutineKind)}>
            <MenuItem value='email'>Email</MenuItem>
            <MenuItem value='report'>Report</MenuItem>
          </Select>
        </FormControl>
        {kind === 'report' ? (
          <FormControl size='small' sx={{ minWidth: 140 }}>
            <InputLabel>Topic</InputLabel>
            <Select
              label='Topic'
              value={topic}
              onChange={event => setTopic(event.target.value as AssistantReportTopic)}
            >
              {TOPICS.map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </Stack>
      <RoutineScheduleFields value={clock} onChange={setClock} />
      <RoutinePeriodField value={period} onChange={setPeriod} />
      <RoutineRecipientFields value={recipients} onChange={setRecipients} />
      <TextField
        label='What should this routine do?'
        value={prompt}
        onChange={event => setPrompt(event.target.value)}
        placeholder='Example: each weekday morning, list trucks sold and trucks added, with stock, VIN, status, and price'
        size='small'
        fullWidth
        multiline
        minRows={3}
        inputProps={{ maxLength: 4000 }}
      />
      <Button
        size='small'
        variant='outlined'
        disabled={
          busy ||
          naming ||
          !routineName.trim() ||
          !prompt.trim() ||
          (clock.schedule === 'once' && !clock.date) ||
          (recipients.userIds.length === 0 && recipients.emails.length === 0)
        }
        onClick={() => void addRoutine()}
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        Save routine
      </Button>
    </Stack>
  )

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth={profile ? 'md' : 'sm'}
      PaperProps={
        profile
          ? {
              sx: {
                height: { xs: '100%', md: 'min(860px, 92vh)' },
                m: { xs: 0, md: 2 },
                display: 'flex',
                flexDirection: 'column',
              },
            }
          : undefined
      }
    >
      <DialogTitle>{profile ? profile.name : 'New assistant'}</DialogTitle>
      {profile ? (
        <Tabs
          value={tab}
          onChange={(_, value: EditorTab) => setTab(value)}
          sx={{ px: 2, flexShrink: 0 }}
        >
          <Tab value='general' label='General' />
          <Tab value='routines' label='Routines' />
          <Tab value='jobs' label='Jobs' />
        </Tabs>
      ) : null}
      <DialogContent sx={profile ? { display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 } : undefined}>
        <Stack spacing={1.5} sx={{ pt: 0.5, flex: profile ? 1 : undefined, minHeight: 0, overflow: profile ? 'auto' : undefined }}>
          {!profile || tab === 'general' ? (
            <>
              <TextField
                label='Name'
                value={name}
                onChange={event => setName(event.target.value)}
                inputProps={{ maxLength: 40 }}
                fullWidth
                size='small'
              />
              <GuideChoices
                question='Which departments should this assistant cover?'
                help='All is the default.'
                options={DEPARTMENT_OPTIONS}
                value={guide.departments}
                onChange={next =>
                  setGuide(current => {
                    const visible = watchOptionsForDepartments(next).map(option => option.id)
                    const watch = isAllSelected(current.watch)
                      ? 'all'
                      : choiceIds(current.watch).filter(id => visible.includes(id)).join(',') || 'all'
                    return { ...current, departments: next, watch }
                  })
                }
              />
              <GuideChoices
                question='What should it focus on?'
                help='Narrower than a department. All is the default.'
                options={watchOptionsForDepartments(guide.departments)}
                value={guide.watch}
                onChange={watch => setGuide(current => ({ ...current, watch }))}
              />
              <FormControl size='small' fullWidth>
                <InputLabel>Tone</InputLabel>
                <Select
                  label='Tone'
                  value={TONE_OPTIONS.some(option => option.id === guide.tone) ? guide.tone : 'direct'}
                  onChange={event => setGuide(current => ({ ...current, tone: event.target.value }))}
                >
                  {TONE_OPTIONS.map(option => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label='Other instructions'
                value={details}
                onChange={event => setDetails(event.target.value)}
                placeholder='Anything else this assistant should keep in mind'
                inputProps={{ maxLength: 2000 }}
                fullWidth
                size='small'
                multiline
                minRows={3}
              />
              {sharing}
            </>
          ) : null}
          {profile && tab === 'routines' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                minHeight: 320,
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', md: 260 },
                  maxHeight: { xs: 220, md: 'none' },
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  borderRight: { md: '1px solid' },
                  borderColor: 'divider',
                  pr: { md: 1 },
                }}
              >
                <Button
                  startIcon={<Add />}
                  onClick={() => {
                    setComposing(true)
                    setSelectedId(null)
                    setShowCode(false)
                    setError(null)
                  }}
                  sx={{ alignSelf: 'flex-start', mb: 1, flexShrink: 0, textTransform: 'none' }}
                >
                  Add routine
                </Button>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                  Times are America/New_York. The checker runs every 15 minutes.
                </Typography>
                <List dense sx={{ overflow: 'auto', flex: 1, maxHeight: { md: 420 } }}>
                  {routines.map(routine => (
                    <ListItemButton
                      key={routine.id}
                      selected={!composing && routine.id === selectedId}
                      onClick={() => {
                        setComposing(false)
                        setSelectedId(routine.id)
                        setShowCode(false)
                      }}
                    >
                      <ListItemText
                        primary={routineTitle(routine)}
                        secondary={routine.enabled ? 'On' : 'Off'}
                        primaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {composing ? (
                  routineForm
                ) : selected && showCode ? (
                  <Stack spacing={1}>
                    <Button size='small' onClick={() => setShowCode(false)} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                      Back to routine
                    </Button>
                    {selected.code.trim() ? (
                      <Box
                        component='pre'
                        sx={{
                          m: 0,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: 12,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatRoutineCode(selected.code)}
                      </Box>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        This routine has no saved draft yet. Older routines did not keep one. Run it once, or save an
                        update and run it, and the draft appears here.
                      </Typography>
                    )}
                  </Stack>
                ) : selected ? (
                  <Stack spacing={1}>
                    <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={1}>
                      <TextField
                        label='Name'
                        value={editName}
                        onChange={event => setEditName(event.target.value)}
                        onBlur={() => {
                          const next = editName.trim()
                          if (!selected || !next || next === selected.name) return
                          setBusy(true)
                          setError(null)
                          void updateAssistantRoutine(selected.id, routinePayload(selected, editRecipients))
                            .catch(caught =>
                              setError(caught instanceof Error ? caught.message : 'Could not rename that routine'),
                            )
                            .finally(() => setBusy(false))
                        }}
                        size='small'
                        fullWidth
                        inputProps={{ maxLength: 60 }}
                      />
                      <Button size='small' onClick={() => setShowCode(true)} sx={{ textTransform: 'none', flexShrink: 0, mt: 0.5 }}>
                        Show code
                      </Button>
                    </Stack>
                    <RoutinePeriodField
                      value={selected.period}
                      onChange={next => {
                        if (next === selected.period) return
                        setBusy(true)
                        setError(null)
                        void updateAssistantRoutine(selected.id, routinePayload({ ...selected, period: next }, editRecipients))
                          .catch(caught =>
                            setError(caught instanceof Error ? caught.message : 'Could not update that data period'),
                          )
                          .finally(() => setBusy(false))
                      }}
                    />
                    <Typography variant='caption' color='text.secondary'>
                      Instructions are locked. The data period is applied when the routine runs. Describe a change to rewrite the instructions.
                    </Typography>
                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                      {selected.prompt}
                    </Typography>
                    <TextField
                      label='Update or enhance'
                      value={enhance}
                      onChange={event => setEnhance(event.target.value)}
                      placeholder='Example: also include trucks that moved to Pending Sale, and keep the advice to one line each'
                      size='small'
                      fullWidth
                      multiline
                      minRows={2}
                      inputProps={{ maxLength: 2000 }}
                    />
                    <Button
                      size='small'
                      variant='outlined'
                      disabled={busy || !enhance.trim()}
                      onClick={() => void saveRoutineUpdate()}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                      Rewrite instructions
                    </Button>
                    <RoutineRecipientFields value={editRecipients} onChange={setEditRecipients} />
                    <Button
                      size='small'
                      disabled={
                        busy || (editRecipients.userIds.length === 0 && editRecipients.emails.length === 0)
                      }
                      onClick={() => void saveRecipients()}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                      Save recipients
                    </Button>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Switch
                        size='small'
                        checked={selected.enabled}
                        inputProps={{ 'aria-label': `Enable ${selected.prompt}` }}
                        onChange={(_, checked) => {
                          void updateAssistantRoutine(selected.id, {
                            kind: selected.kind,
                            name: selected.name,
                            prompt: selected.prompt,
                            period: selected.period,
                            schedule: selected.schedule,
                            times: selected.times,
                            days: selected.days,
                            date: selected.date,
                            recipientUserIds: selected.recipientUserIds,
                            recipientEmails: selected.recipientEmails,
                            reportTopic: selected.reportTopic,
                            enabled: checked,
                          }).catch(caught =>
                            setError(caught instanceof Error ? caught.message : 'Could not update that routine'),
                          )
                        }}
                      />
                      <Typography variant='body2'>{selected.enabled ? 'Enabled' : 'Off'}</Typography>
                      <Box sx={{ flex: 1 }} />
                      <Button
                        size='small'
                        disabled={busy || runDisabled(selected)}
                        onClick={() => runNow(selected.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        Run now
                      </Button>
                      <IconButton
                        size='small'
                        aria-label='Delete routine'
                        onClick={() => {
                          void deleteAssistantRoutine(selected.id)
                            .then(() => setSelectedId(null))
                            .catch(caught =>
                              setError(caught instanceof Error ? caught.message : 'Could not delete that routine'),
                            )
                        }}
                      >
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    </Stack>
                    {selected.lastStatus ? (
                      <Typography variant='caption' color='text.secondary'>
                        Last result: {selected.lastStatus}
                      </Typography>
                    ) : null}
                  </Stack>
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    Select a routine, or choose Add routine.
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null}
          {profile && tab === 'jobs' ? (
            <Stack spacing={1}>
              {runs.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  No jobs yet. Run now shows progress here.
                </Typography>
              ) : (
                runs.map(run => (
                  <Box key={run.id} sx={{ py: 0.5 }}>
                    <Typography variant='body2'>
                      {run.label} · {STATUS_LABEL[run.status]}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {run.startedAt
                        ? new Date(run.startedAt).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : 'Starting'}
                    </Typography>
                    {run.error ? (
                      <Typography variant='caption' color='error' sx={{ display: 'block' }}>
                        {run.error}
                      </Typography>
                    ) : null}
                  </Box>
                ))
              )}
            </Stack>
          ) : null}
          {error ? (
            <Typography variant='body2' color='error'>
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {profile && isOwner && tab === 'general' ? (
          <Button
            color='inherit'
            disabled={busy}
            onClick={() => {
              setBusy(true)
              void deleteAssistantProfile(profile.id)
                .then(() => onDeleted())
                .catch(caught =>
                  setError(caught instanceof Error ? caught.message : 'Could not delete that assistant'),
                )
                .finally(() => setBusy(false))
            }}
          >
            Delete
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        {!profile || tab === 'general' ? (
          <Button variant='contained' disabled={busy || !name.trim()} onClick={() => void saveProfile()}>
            Save
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
